import React, { useEffect, useState } from "react";
import {
  BENCHMARK_SUB_COLS,
  formatBenchmarkMetricShortLabel,
  formatBenchmarkStatValue,
} from "../../benchmarkMetricConfig";
import styles from "./BreakdownTable.module.scss";

const fmtCell = (num, col) => {
  if (num == null) return "—";
  return formatBenchmarkStatValue(num, col);
};

const getAbbreviation = (name) => {
  const skip = new Set(["of", "the", "at", "and", "–"]);
  return name
    .split(/[\s,]+/)
    .filter(Boolean)
    .filter((w) => !skip.has(w.toLowerCase()))
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 3);
};

const InstitutionLogo = ({ photoUrl, fallbackText }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [photoUrl]);
  if (photoUrl && !failed) {
    return (
      <img
        src={photoUrl}
        alt=""
        className={styles["logo-img"]}
        loading="lazy"
        onError={() => setFailed(true)}
        data-report-strip="true"
      />
    );
  }
  return (
    <div className={styles["logo-fallback"]} data-report-strip="true">
      {fallbackText}
    </div>
  );
};

/**
 * Default layout: rows = research topics, columns = institutions × metrics.
 * `entityRows` layout: rows = entities (institution / researcher / unit / venue),
 * columns = research topics × metrics. Used when there are typically more
 * keywords than entities or when the user explicitly wants an entity-major view.
 */
const BreakdownTable = ({
  rawData,
  institutionIds,
  layout = "keywordRows",
  entityIdKey = "institution_id",
  entityLabel = "Institution",
  subCols = BENCHMARK_SUB_COLS,
}) => {
  if (!rawData || rawData.length === 0) return null;

  const keywords = rawData.map((entry) => Object.keys(entry)[0]);

  const lookups = rawData.map((entry) => {
    const keyword = Object.keys(entry)[0];
    const map = {};
    (entry[keyword] || []).forEach((item) => {
      const id = item?.[entityIdKey] ?? item?.institution_id;
      if (id != null) map[id] = item;
    });
    return map;
  });

  const firstLookup = lookups[0] || {};
  const orderedEntities = institutionIds
    .map((id) => firstLookup[id])
    .filter(Boolean);

  if (layout === "entityRows") {
    const keywordColWidth = "200px";
    const metricColWidth = "180px";
    const entityColCount = Math.max(orderedEntities.length, 1);
    const entityColWidth = `calc((100% - 380px) / ${entityColCount})`;

    return (
      <div className={styles["breakdown"]}>
        <div className={styles["breakdown__header"]} data-report-strip="true">
          <div>
            <h3 className={styles["breakdown__title"]}>
              Reseach Topics Specific Breakdown
            </h3>
          </div>
          <span className={styles["breakdown__scroll-hint"]}>
            ⟶ Scroll horizontally for details
          </span>
        </div>
        <div className={styles["table-scroll"]}>
          <table className={styles["table"]}>
            <colgroup>
              <col style={{ width: keywordColWidth }} />
              <col style={{ width: metricColWidth }} />
              {orderedEntities.map((entity) => {
                const id = entity?.[entityIdKey] ?? entity?.institution_id;
                return <col key={`col-${id}`} style={{ width: entityColWidth }} />;
              })}
            </colgroup>
            <thead>
              <tr className={styles["header-row-top"]}>
                <th
                  className={styles["header-keyword-corner"]}
                  colSpan={2}
                  style={{ width: "380px", minWidth: "380px" }}
                >
                  Research topics
                </th>
                {orderedEntities.map((entity) => {
                  const id = entity?.[entityIdKey] ?? entity?.institution_id;
                  return (
                    <th key={id} className={styles["header-inst"]}>
                      <div className={styles["header-inst__inner"]}>
                        <InstitutionLogo
                          photoUrl={entity.photo_url}
                          fallbackText={getAbbreviation(entity.name || "")}
                        />
                        <span className={styles["header-inst__name"]}>
                          {entity.name}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {keywords.map((kw, kwIdx) =>
                subCols.map((col, metricIdx) => (
                  <tr
                    key={`${kw}-${col.key}`}
                    className={`${styles["body-row"]} ${
                      kwIdx % 2 === 1 ? styles["body-row--alt"] : ""
                    }`}
                  >
                    {metricIdx === 0 ? (
                      <td
                        className={styles["kw-cell"]}
                        rowSpan={subCols.length}
                        style={{ width: "200px", minWidth: "200px" }}
                      >
                        {kw}
                      </td>
                    ) : null}
                    <td
                      className={styles["metric-cell"]}
                      style={{ width: "180px", minWidth: "180px" }}
                    >
                      {col.label}
                    </td>
                    {orderedEntities.map((entity) => {
                      const id = entity?.[entityIdKey] ?? entity?.institution_id;
                      const data = lookups[kwIdx]?.[id] || {};
                      return (
                        <td key={`${kw}-${col.key}-${id}`} className={styles["data-cell"]}>
                          {fmtCell(data[col.key], col)}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className={styles["breakdown"]}>
      <div className={styles["breakdown__header"]}>
        <div>
          <h3 className={styles["breakdown__title"]}>
            Reseach Topics Specific Breakdown
          </h3>
        </div>
        <span className={styles["breakdown__scroll-hint"]}>
          ⟶ Scroll horizontally for details
        </span>
      </div>
      <div className={styles["table-scroll"]}>
        <table className={styles["table"]}>
          <thead>
            <tr className={styles["header-row-top"]}>
              <th
                className={styles["header-keyword-corner"]}
                rowSpan={2}
              >
                Research topics
              </th>
              {orderedEntities.map((inst) => {
                const id = inst?.[entityIdKey] ?? inst?.institution_id;
                return (
                  <th
                    key={id}
                    className={styles["header-inst"]}
                    colSpan={subCols.length}
                  >
                    <div className={styles["header-inst__inner"]}>
                      <InstitutionLogo
                        photoUrl={inst.photo_url}
                        fallbackText={getAbbreviation(inst.name || "")}
                      />
                      <span className={styles["header-inst__name"]}>
                        {inst.name}
                      </span>
                    </div>
                  </th>
                );
              })}
            </tr>
            <tr className={styles["header-row-sub"]}>
              {orderedEntities.map((inst) => {
                const id = inst?.[entityIdKey] ?? inst?.institution_id;
                return subCols.map((col) => (
                  <th
                    key={`${id}-${col.key}`}
                    className={styles["header-sub"]}
                  >
                    {formatBenchmarkMetricShortLabel(col.label)}
                  </th>
                ));
              })}
            </tr>
          </thead>
          <tbody>
            {keywords.map((kw, kwIdx) => (
              <tr
                key={kw}
                className={`${styles["body-row"]} ${
                  kwIdx % 2 === 1 ? styles["body-row--alt"] : ""
                }`}
              >
                <td className={styles["kw-cell"]}>{kw}</td>
                {orderedEntities.map((inst) => {
                  const id = inst?.[entityIdKey] ?? inst?.institution_id;
                  const data = lookups[kwIdx]?.[id] || {};
                  return subCols.map((col) => (
                    <td
                      key={`${id}-${kw}-${col.key}`}
                      className={styles["data-cell"]}
                    >
                      {fmtCell(data[col.key], col)}
                    </td>
                  ));
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BreakdownTable;
